import { useState } from 'react'
import { Icon, Image, Text, VStack, type BoxProps, type ImageProps } from '@chakra-ui/react'

interface StreetViewImageProps extends BoxProps {
  // Backend Home.imageUrl. '' means Street View was fetched but suppressed (e.g.
  // the nearest panorama is too far to show the building) -> show a placeholder,
  // NOT an address-based re-fetch that would surface the same wrong street.
  // null/undefined falls back to a client-built Street View for the address.
  imageUrl?: string | null
  address: string
  svSize?: string
  alt?: string
  showLabel?: boolean
}

const buildStreetViewUrl = (address: string, size: string): string | null => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  if (!apiKey) return null
  return `https://maps.googleapis.com/maps/api/streetview?size=${size}&location=${encodeURIComponent(address)}&key=${apiKey}`
}

export const StreetViewImage = ({
  imageUrl,
  address,
  svSize = '400x300',
  alt = 'Street View',
  showLabel = true,
  ...boxProps
}: StreetViewImageProps) => {
  const [errored, setErrored] = useState(false)

  const src = imageUrl === '' ? null : imageUrl || buildStreetViewUrl(address, svSize)

  if (!src || errored) {
    return (
      <VStack gap={1} justify="center" bg="gray.100" color="gray.400"
        overflow="hidden" textAlign="center" p={2} minH="80px" {...boxProps}>
        <Icon boxSize={6}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </Icon>
        {showLabel && <Text fontSize="xs" lineHeight="1.2">Street View unavailable</Text>}
      </VStack>
    )
  }

  return (
    <Image src={src} alt={alt} {...(boxProps as ImageProps)} onError={() => setErrored(true)} />
  )
}
